'use client'

import { useMediaQuery } from 'react-responsive'
import { Heading, Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import type { Media, Theme } from '../../types/props.js'

/**
 * KSP 卡片项数据结构
 */
export interface KspCardItem {
  /** 标题 */
  title?: string
  /** 描述 */
  desc?: string
  /** 移动端图片 */
  mobImage?: Media
  /** 移动端视频 */
  mobVideo?: Media

  /** 平板端图片 (≥768px) */
  tabletImage?: Media
  /** 平板端视频 (≥768px) */
  tabletVideo?: Media

  /** 小桌面端图片 (≥1025px) */
  laptopImage?: Media
  /** 小桌面端视频 (≥1025px) */
  laptopVideo?: Media

  /** 桌面端图片 (≥1440px) */
  desktopImage?: Media
  /** 桌面端视频 (≥1440px) */
  desktopVideo?: Media
  /** 大桌面端图片 */
  image?: Media
  /** 大桌面端视频 */
  video?: Media
}

/**
 * KSP 数据结构
 */
export interface KspData {
  /** 主标题 */
  title?: string
  /** KSP 卡片列表（需要 7 个项） */
  items: KspCardItem[]
  /** 主题，默认 light */
  theme?: Theme
}

/**
 * Ksp 组件语义化类名配置
 */
export interface KspClassNames {
  /** 整体容器类名 */
  root?: string
  /** 主标题类名 */
  title?: string
  /** 卡片布局容器类名 */
  layout?: string
  /** 单个卡片类名 */
  card?: string
  /** 卡片视频类名 */
  cardVideo?: string
  /** 卡片图片类名 */
  cardImage?: string
  /** 卡片图片 img 元素类名 */
  cardImageImg?: string
  /** 卡片内容区类名 */
  cardContent?: string
  /** 卡片标题类名 */
  cardTitle?: string
  /** 卡片描述类名 */
  cardDesc?: string
}

/**
 * Ksp 组件 Props
 */
export interface KspProps extends Omit<React.HTMLAttributes<HTMLElement>, 'className'> {
  /** KSP 数据 */
  data: KspData
  /** 自定义类名 */
  className?: string
  /** 语义化类名配置 */
  classNames?: KspClassNames
}

interface ImageCardProps extends KspCardItem {
  /** 自定义类名 */
  className?: string
  /** 主题 */
  theme?: Theme
  /** 语义化类名配置 */
  classNames?: KspClassNames
}

const ImageCard = ({
  title,
  desc,
  // 新的断点媒体属性
  mobImage,
  mobVideo,
  tabletImage,
  tabletVideo,
  laptopImage,
  laptopVideo,
  desktopImage,
  desktopVideo,
  // 保留旧属性作为大屏默认值
  image,
  video,
  className,
  theme,
  classNames,
}: ImageCardProps) => {
  // 响应式断点检测
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const isTablet = useMediaQuery({ query: '(min-width: 768px) and (max-width: 1024px)' })
  const isLaptop = useMediaQuery({ query: '(min-width: 1025px) and (max-width: 1439px)' })
  // desktop 及以上使用默认

  // 获取当前断点的视频源（带 fallback）
  const getCurrentVideo = (): Media | undefined => {
    if (isMobile) return mobVideo || tabletVideo || laptopVideo || desktopVideo || video
    if (isTablet) return tabletVideo || laptopVideo || desktopVideo || video
    if (isLaptop) return laptopVideo || desktopVideo || video
    return desktopVideo || video
  }

  // 获取当前断点的封面图
  const getCurrentPoster = (): Media | undefined => {
    if (isMobile) return mobImage || tabletImage || laptopImage || desktopImage || image
    if (isTablet) return tabletImage || laptopImage || desktopImage || image
    if (isLaptop) return laptopImage || desktopImage || image
    return desktopImage || image
  }

  const currentVideo = getCurrentVideo()
  const currentPoster = getCurrentPoster()

  // 构建响应式图片源
  const buildImageSource = (): string | undefined => {
    const sources: string[] = []
    const lgDesktop = image?.url || desktopImage?.url
    const desktop = desktopImage?.url || image?.url
    const laptop = laptopImage?.url || desktop
    const tablet = tabletImage?.url || laptop
    const mobile = mobImage?.url || tablet

    if (lgDesktop) sources.push(lgDesktop)
    if (desktop && desktop !== lgDesktop) sources.push(`${desktop} 1920`)
    if (laptop && laptop !== desktop) sources.push(`${laptop} 1440`)
    if (tablet && tablet !== laptop) sources.push(`${tablet} 1025`)
    if (mobile && mobile !== tablet) sources.push(`${mobile} 768`)

    return sources.length > 0 ? sources.join(', ') : undefined
  }

  // 判断是否有视频
  const hasVideo = !!currentVideo?.url

  // 判断是否有任何图片
  const hasAnyImage = !!(mobImage?.url || tabletImage?.url || laptopImage?.url || desktopImage?.url || image?.url)

  return (
    <div
      className={cn(
        'ksp-card rounded-box text-info-primary relative overflow-hidden',
        {
          'aiui-dark': theme === 'dark',
        },
        className,
        classNames?.card
      )}
    >
      {hasVideo ? (
        <video
          playsInline
          autoPlay
          muted
          loop
          src={currentVideo?.url}
          poster={currentPoster?.url}
          className={cn('ksp-card-video absolute bottom-0 w-full object-cover', classNames?.cardVideo)}
        />
      ) : (
        hasAnyImage && (
          <Picture
            source={buildImageSource()}
            alt={currentPoster?.alt || ''}
            className={cn('ksp-card-image size-full', classNames?.cardImage)}
            imgClassName={cn('size-full object-cover object-bottom', classNames?.cardImageImg)}
          />
        )
      )}
      <div className={cn('ksp-card-content desktop:p-6 lg-desktop:p-8 p-4', classNames?.cardContent)}>
        <Heading
          size={3}
          html={title}
          className={cn('ksp-card-title tablet:block hidden w-full', classNames?.cardTitle)}
        />
        <Heading
          size={2}
          html={title}
          className={cn(
            'ksp-card-title tablet:hidden tablet:w-[340px] xs-tablet:text-[16px] block w-full',
            classNames?.cardTitle
          )}
        />
        <Text
          size={2}
          html={desc}
          className={cn(
            'ksp-card-desc text-info-primary tablet:block tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-[14px]',
            classNames?.cardDesc
          )}
        />
      </div>
    </div>
  )
}

/**
 * 布局配置映射表
 * 根据 items 数量和索引位置，定义每个卡片的栅格布局类名
 */
const LAYOUT_CONFIG: Record<number, Record<number, string>> = {
  4: {
    0: 'col-span-2 laptop:col-span-6 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[440] laptop:aspect-h-[240] desktop:aspect-w-[648] desktop:aspect-h-[256] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[320]',
    1: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    2: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    3: 'col-span-2 laptop:col-span-6 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[440] laptop:aspect-h-[240] desktop:aspect-w-[648] desktop:aspect-h-[256] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[320]',
  },
  5: {
    0: 'col-span-2 laptop:col-span-6 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[440] laptop:aspect-h-[240] desktop:aspect-w-[648] desktop:aspect-h-[256] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[320]',
    1: 'col-span-2 tablet:col-span-1 laptop:col-span-4 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    2: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    3: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    4: 'col-span-2 tablet:col-span-1 laptop:col-span-3 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
  },
  6: {
    0: 'col-span-2 tablet:col-span-1 laptop:col-span-4 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    1: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    2: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    3: 'col-span-2 tablet:col-span-1 laptop:col-span-3 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    4: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    5: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
  },
  7: {
    0: 'col-span-2 laptop:col-span-6 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[440] laptop:aspect-h-[240] desktop:aspect-w-[648] desktop:aspect-h-[256] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[320]',
    1: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    2: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    3: 'col-span-2 tablet:col-span-1 laptop:col-span-4 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    4: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    5: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    6: 'col-span-2 laptop:col-span-6 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[440] laptop:aspect-h-[240] desktop:aspect-w-[648] desktop:aspect-h-[256] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[320]',
  },
  8: {
    0: 'col-span-2 laptop:col-span-6 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[440] laptop:aspect-h-[240] desktop:aspect-w-[648] desktop:aspect-h-[256] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[320]',
    1: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    2: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    3: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    4: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    5: 'col-span-2 tablet:col-span-1 laptop:col-span-3 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    6: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    7: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
  },
  9: {
    0: 'col-span-2 laptop:col-span-4 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    1: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    2: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    3: 'col-span-1 tablet:col-span-2 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    4: 'col-span-1 laptop:col-span-4 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
    5: 'col-span-2 tablet:col-span-1 laptop:col-span-3 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    6: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    7: 'col-span-1 laptop:col-span-3 aspect-w-[173] aspect-h-[240] tablet:aspect-w-[227] tablet:aspect-h-[240] laptop:aspect-w-[212] laptop:aspect-h-[240] desktop:aspect-w-[316] desktop:aspect-h-[256] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[320]',
    8: 'col-span-2 laptop:col-span-4 aspect-w-[358] aspect-h-[240] tablet:aspect-w-[465] tablet:aspect-h-[240] laptop:aspect-w-[288] laptop:aspect-h-[240] desktop:aspect-w-[426] desktop:aspect-h-[256] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[320]',
  },
}

/**
 * 获取卡片的布局类名
 * @param index - 卡片索引
 * @param totalCount - 卡片总数
 * @returns 布局类名
 */
const getCardLayoutClass = (index: number, totalCount: number): string => {
  return LAYOUT_CONFIG[totalCount]?.[index] || ''
}

/**
 * Ksp - Key Selling Point 关键卖点组件
 *
 * 展示产品的关键卖点，支持图片和视频，具有复杂的响应式布局。
 * 布局支持 4-9 个卡片项，分别对应不同尺寸和位置。
 *
 * @example
 * ```tsx
 * <Ksp
 *   data={{
 *     title: '为什么选择我们',
 *     items: [
 *       {
 *         title: '高性能',
 *         desc: '强大的处理能力',
 *         image: '/desktop.jpg',
 *         mobImage: '/mobile.jpg'
 *       },
 *       // ... 需要 4-9 个项
 *     ]
 *   }}
 * />
 * ```
 */
function Ksp({ data, className, classNames, ...rest }: KspProps) {
  const { title, items, theme = 'light' } = data
  return (
    <section
      {...rest}
      data-ui-component-id="ksp"
      className={cn(
        'ksp-container text-info-primary laptop:scroll-mt-[46px] flex scroll-mt-[108px] flex-col justify-center',
        {
          'aiui-dark': theme === 'dark',
        },
        className,
        classNames?.root
      )}
    >
      <Heading
        as="h1"
        size={4}
        html={title}
        className={cn('ksp-title laptop:text-center text-left', classNames?.title)}
      />
      <div
        className={cn(
          'ksp-layout tablet:grid-cols-3 laptop:grid-cols-10 laptop:gap-4 mt-[24px] grid grid-cols-2 gap-3',
          classNames?.layout
        )}
      >
        {items.map((item, index) => (
          <ImageCard
            key={index}
            {...item}
            className={getCardLayoutClass(index, items.length)}
            classNames={classNames}
          />
        ))}
      </div>
    </section>
  )
}

export default withLayout(Ksp)
